import { useCallback, useState, useSyncExternalStore } from "react";

/** A value in localStorage, for the filter bar. */
export function useStored<T extends string = string>(key: string, fallback: T): [T, (value: T) => void] {
	const [value, setValue] = useState<T>(() => {
		try {
			return (localStorage.getItem(key) as T) ?? fallback;
		} catch {
			// Private mode, or storage blocked. The panel works without it.
			return fallback;
		}
	});

	const set = useCallback(
		(next: T) => {
			setValue(next);
			try {
				localStorage.setItem(key, next);
			} catch {
				/* not worth surfacing */
			}
		},
		[key],
	);

	return [value, set];
}

/**
 * Whether a CSS media query matches, kept in step with the viewport.
 *
 * `useSyncExternalStore` rather than an effect: the first render has to agree
 * with the query, and a state set from an effect gives it one frame of the
 * wrong answer — which for a layout question is a visible flash.
 */
export function useMedia(query: string): boolean {
	const subscribe = useCallback(
		(onChange: () => void) => {
			if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
			const list = window.matchMedia(query);
			list.addEventListener("change", onChange);
			return () => list.removeEventListener("change", onChange);
		},
		[query],
	);
	const read = useCallback(() => {
		if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
		return window.matchMedia(query).matches;
	}, [query]);
	return useSyncExternalStore(subscribe, read, () => false);
}
